import { forwardRef, useState } from 'react'

import { Input as AntdInput, InputProps, InputRef } from 'antd'
import styled, { css } from 'styled-components'

import Eye from 'src/Assets/Svg/common/Eye.svg?react'
import EyeCrossed from 'src/Assets/Svg/common/EyeCrossed.svg?react'
import { variables } from 'src/Styled/variables'

const commonStyles = css`
  font-size: ${variables.fontSize12};
  opacity: 1;
  top: 7px;
  left: 16px;
  transition: all 0.2s ease;
  margin: 0;
  z-index: 2;
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;

  &:focus-within p {
    ${commonStyles}
  }

  &:has(input:not(:placeholder-shown)) {
    p {
      ${commonStyles}
    }

    .ant-input-affix-wrapper {
      padding-top: 24px !important;
      padding-bottom: 7px !important;
    }

    .ant-input-suffix {
      display: flex;
    }
  }
`

const Label = styled.p<{
  $disabled?: boolean
}>`
  font-family: 'Segoe UI';
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.lightGrey : theme.color.slateGrey} !important;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  opacity: 0;
  transition: all 0.2s ease;
  position: absolute;
  top: 27px;
  left: 18px;
  z-index: 0;
`

const ErrorMessage = styled.div`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.mediumRed};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  margin-top: 5px;
`

const ValidationMessage = styled.div`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.mediumOrange};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  margin-top: 5px;
`

const StyledInput = styled(AntdInput)<{
  $isError?: boolean
  $isValidationError?: boolean
}>`
  color: ${({ theme }) => theme.color.darkGrey};
  background-color: ${({ theme }) => theme.color.white} !important;
  border-radius: 16px;
  padding: 16px !important;

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.color.white} inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.color.darkGrey} !important;
  }

  border: ${({ theme, $isError, $isValidationError }) => {
    if ($isError) {
      return `1px solid ${theme.color.mediumRed}`
    }
    if ($isValidationError) {
      return `1px solid ${theme.color.mediumOrange}`
    }
    return `1px solid ${theme.color.lightGrey}`
  }} !important;
  position: relative;

  .ant-input {
    padding: 0;
    padding-right: 28px;
    background-color: ${({ theme }) => theme.color.white} !important;
    color: ${({ theme }) => theme.color.darkGrey};
  }

  .ant-input-suffix {
    position: absolute;
    right: 16px;
    top: 0;
    bottom: 0;
    display: none;
    align-items: center;
    margin: 0;
  }

  &:hover:not(.ant-input-affix-wrapper-disabled) {
    background-color: ${({ theme }) => theme.color.hoverWhite} !important;

    input {
      background-color: ${({ theme }) => theme.color.hoverWhite} !important;
    }
  }

  &.ant-input-affix-wrapper-focused {
    background-color: ${({ theme }) => theme.color.white} !important;
    padding-top: 24px !important;
    padding-bottom: 7px !important;

    border: ${({ theme, $isError, $isValidationError }) => {
      if ($isError) {
        return `1px solid ${theme.color.mediumRed}`
      }
      if ($isValidationError) {
        return `1px solid ${theme.color.mediumOrange}`
      }
      return `1px solid ${theme.color.brandGreen}`
    }} !important;

    input {
      background-color: ${({ theme }) => theme.color.white} !important;
    }
  }

  input {
    background-color: ${({ theme }) => theme.color.white} !important;
    color: ${({ theme }) => theme.color.darkGrey};

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.color.white} inset !important;
      -webkit-text-fill-color: ${({ theme }) =>
        theme.color.darkGrey} !important;
    }

    &::placeholder {
      color: ${({ theme }) => theme.color.slateGrey};
      font-family: 'Segoe UI';
      line-height: 150%;
      font-size: ${variables.fontSize14};
      font-weight: ${variables.fontWeight400};
      transition: all 0.2s ease;
    }

    &:focus-visible {
      box-shadow: none;
      background-color: ${({ theme }) => theme.color.white} !important;

      &::placeholder {
        opacity: 0;
        transition: all 0.2s ease;
      }
    }
  }

  &.ant-input-affix-wrapper-disabled {
    border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`} !important;
    background-color: ${({ theme }) => theme.color.white};

    .ant-input {
      color: ${({ theme }) => theme.color.lightGrey};

      &::placeholder {
        color: ${({ theme }) => theme.color.lightGrey};
      }
    }
  }

  &&:active {
    background-color: ${({ theme }) => theme.color.white};
  }

  &&.ant-input-disabled {
    background-color: ${({ theme }) => theme.color.white};
    color: ${({ theme }) => theme.color.lightGrey};

    &::placeholder {
      color: ${({ theme }) => theme.color.lightGrey};
    }
  }
`

const ToggleButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.darkGrey};
  line-height: 0;

  svg path {
    fill: currentColor;
  }

  &:disabled {
    color: ${({ theme }) => theme.color.lightGrey};
    cursor: not-allowed;
  }
`

export type PasswordInputProps = Omit<InputProps, 'type'> & {
  error?: string | null
  validationError?: string
  className?: string
}

export const PasswordInput = forwardRef<InputRef, PasswordInputProps>(
  (props, ref) => {
    const {
      placeholder,
      error,
      validationError,
      className,
      disabled,
      ...rest
    } = props
    const [showPassword, setShowPassword] = useState(false)

    const visibilityToggle = (
      <ToggleButton
        type='button'
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShowPassword((prev) => !prev)}
      >
        {showPassword ? <Eye /> : <EyeCrossed />}
      </ToggleButton>
    )

    return (
      <Wrapper>
        <Label $disabled={disabled}>{placeholder}</Label>
        <StyledInput
          {...rest}
          placeholder={placeholder}
          disabled={disabled}
          type={showPassword ? 'text' : 'password'}
          suffix={visibilityToggle}
          $isError={Boolean(error)}
          $isValidationError={Boolean(validationError)}
          ref={ref}
          className={className}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {validationError && (
          <ValidationMessage>{validationError}</ValidationMessage>
        )}
      </Wrapper>
    )
  },
)
